/**
 * Video Tutorial Registry
 * 
 * Centralized video tutorial data and types
 * 
 * Version: 6.2.0
 */

// Tutorial Categories
export type TutorialCategory = 
  | 'getting-started'  // Beginner tutorials
  | 'strategies'       // Strategy tutorials
  | 'brokers'          // Broker connection
  | 'analytics'        // Analytics & reports
  | 'advanced'         // Advanced features
  | 'tips'             // Tips & tricks
  | 'options'          // Options trading tutorials
  | 'ai'               // AI features tutorials
  | 'tradetron'        // TradetrON integration
  | 'pine-script'      // Pine Script tutorials

// Tutorial Difficulty
export type TutorialDifficulty = 'beginner' | 'intermediate' | 'advanced'

// Video Resource
export interface VideoResource {
  type: 'youtube' | 'vimeo' | 'local' | 'external'
  url: string
  thumbnail?: string
}

// Tutorial Interface
export interface Tutorial {
  id: string
  title: string
  description: string
  category: TutorialCategory
  difficulty: TutorialDifficulty
  duration: string          // e.g., "5:30" or "10 min"
  thumbnail: string
  video: VideoResource
  prerequisites?: string[] // IDs of prerequisite tutorials
  relatedTutorials?: string[] // IDs of related tutorials
  tags: string[]
  featured: boolean
  publishedAt: string
}

// Tutorial Progress
export interface TutorialProgress {
  tutorialId: string
  watched: boolean
  completed: boolean
  lastPosition?: number    // Video timestamp in seconds
  watchedAt?: Date
}

// Sample thumbnail placeholder
const PLACEHOLDER_THUMBNAIL = 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=640&h=360&fit=crop'

// Tutorial Registry
export const TUTORIALS: Tutorial[] = [
  // Getting Started
  {
    id: 'welcome-to-zobull',
    title: 'Welcome to ZO Bull',
    description: 'Get started with ZO Bull - your AI-powered algo trading platform. Learn about the key features and how to navigate the dashboard.',
    category: 'getting-started',
    difficulty: 'beginner',
    duration: '3:45',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', // Replace with actual video
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['intro', 'dashboard', 'overview'],
    featured: true,
    publishedAt: '2026-01-15'
  },
  {
    id: 'quick-start-guide',
    title: 'Quick Start Guide',
    description: 'Get up and running with ZO Bull in under 10 minutes. Connect your broker, create your first strategy, and make your first trade.',
    category: 'getting-started',
    difficulty: 'beginner',
    duration: '8:20',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    prerequisites: ['welcome-to-zobull'],
    tags: ['setup', 'quickstart', 'first-trade'],
    featured: true,
    publishedAt: '2026-01-20'
  },
  {
    id: 'understanding-dashboard',
    title: 'Understanding the Dashboard',
    description: 'Deep dive into the ZO Bull dashboard. Learn about P&L tracking, performance charts, and quick actions.',
    category: 'getting-started',
    difficulty: 'beginner',
    duration: '6:15',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    prerequisites: ['welcome-to-zobull'],
    tags: ['dashboard', 'ui', 'navigation'],
    featured: false,
    publishedAt: '2026-02-01'
  },
  {
    id: 'account-setup',
    title: 'Account Setup & Security',
    description: 'Secure your ZO Bull account with 2FA, set up notifications, and configure your profile settings.',
    category: 'getting-started',
    difficulty: 'beginner',
    duration: '5:30',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['security', '2fa', 'settings'],
    featured: false,
    publishedAt: '2026-02-05'
  },

  // Strategies
  {
    id: 'creating-strategy',
    title: 'Creating Your First Strategy',
    description: 'Learn how to build a trading strategy from scratch using our visual strategy builder.',
    category: 'strategies',
    difficulty: 'beginner',
    duration: '12:00',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    prerequisites: ['quick-start-guide'],
    tags: ['strategy-builder', 'conditions', 'indicators'],
    featured: true,
    publishedAt: '2026-02-10'
  },
  {
    id: 'using-indicators',
    title: 'Using Technical Indicators',
    description: 'Master technical indicators like RSI, MACD, Bollinger Bands, and more for building powerful strategies.',
    category: 'strategies',
    difficulty: 'intermediate',
    duration: '15:30',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    prerequisites: ['creating-strategy'],
    tags: ['indicators', 'RSI', 'MACD', 'technical-analysis'],
    featured: true,
    publishedAt: '2026-02-15'
  },
  {
    id: 'backtesting-basics',
    title: 'Backtesting Your Strategies',
    description: 'Learn how to backtest your trading strategies using historical data to validate performance.',
    category: 'strategies',
    difficulty: 'intermediate',
    duration: '10:45',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    prerequisites: ['using-indicators'],
    tags: ['backtesting', 'historical-data', 'performance'],
    featured: false,
    publishedAt: '2026-02-20'
  },
  {
    id: 'advanced-conditions',
    title: 'Advanced Strategy Conditions',
    description: 'Create complex trading conditions with AND/OR logic, nested conditions, and multi-leg strategies.',
    category: 'strategies',
    difficulty: 'advanced',
    duration: '18:00',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    prerequisites: ['using-indicators'],
    tags: ['advanced', 'conditions', 'multi-leg'],
    featured: false,
    publishedAt: '2026-03-01'
  },

  // Brokers
  {
    id: 'connecting-zerodha',
    title: 'Connecting Zerodha',
    description: 'Step-by-step guide to connect your Zerodha account with ZO Bull for automated trading.',
    category: 'brokers',
    difficulty: 'beginner',
    duration: '7:00',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['zerodha', 'kite', 'api', 'broker'],
    featured: true,
    publishedAt: '2026-03-05'
  },
  {
    id: 'connecting-angel-one',
    title: 'Connecting Angel One',
    description: 'Learn how to connect Angel One Smart API with ZO Bull for seamless trading.',
    category: 'brokers',
    difficulty: 'beginner',
    duration: '6:30',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['angel-one', 'smart-api', 'broker'],
    featured: false,
    publishedAt: '2026-03-10'
  },
  {
    id: 'api-key-setup',
    title: 'API Key Configuration',
    description: 'Understanding API keys, vendor codes, and how to configure them securely.',
    category: 'brokers',
    difficulty: 'intermediate',
    duration: '8:45',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    prerequisites: ['connecting-zerodha'],
    tags: ['api', 'security', 'configuration'],
    featured: false,
    publishedAt: '2026-03-15'
  },

  // Analytics
  {
    id: 'reading-analytics',
    title: 'Reading Your Analytics',
    description: 'Understand your trading performance with detailed analytics, P&L reports, and metrics.',
    category: 'analytics',
    difficulty: 'beginner',
    duration: '9:00',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['analytics', 'pnl', 'performance'],
    featured: false,
    publishedAt: '2026-03-20'
  },
  {
    id: 'exporting-reports',
    title: 'Exporting Reports',
    description: 'Generate and export detailed trading reports for tax filing and record keeping.',
    category: 'analytics',
    difficulty: 'beginner',
    duration: '4:30',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['export', 'reports', 'tax'],
    featured: false,
    publishedAt: '2026-03-25'
  },

  // Advanced
  {
    id: 'paper-trading',
    title: 'Paper Trading Mode',
    description: 'Practice trading without risking real money using our paper trading simulator.',
    category: 'advanced',
    difficulty: 'beginner',
    duration: '6:00',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['paper-trading', 'practice', 'simulator'],
    featured: true,
    publishedAt: '2026-04-01'
  },
  {
    id: 'risk-management',
    title: 'Risk Management Strategies',
    description: 'Learn essential risk management techniques to protect your capital while trading.',
    category: 'advanced',
    difficulty: 'advanced',
    duration: '14:00',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['risk', 'management', 'capital'],
    featured: true,
    publishedAt: '2026-04-05'
  },

  // Tips
  {
    id: 'keyboard-shortcuts',
    title: 'Keyboard Shortcuts',
    description: 'Speed up your workflow with ZO Bull keyboard shortcuts for power users.',
    category: 'tips',
    difficulty: 'beginner',
    duration: '3:30',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['shortcuts', 'productivity', 'tips'],
    featured: false,
    publishedAt: '2026-04-10'
  },
  {
    id: 'notifications-setup',
    title: 'Setting Up Notifications',
    description: 'Configure desktop and mobile notifications for trade alerts and system updates.',
    category: 'tips',
    difficulty: 'beginner',
    duration: '4:15',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['notifications', 'alerts', 'tips'],
    featured: false,
    publishedAt: '2026-04-15'
  },
  {
    id: 'pcr-analysis',
    title: 'Put-Call Ratio (PCR) Analysis',
    description: 'Learn how to use PCR to gauge market sentiment and identify potential reversals in options trading.',
    category: 'options',
    difficulty: 'intermediate',
    duration: '10:15',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['pcr', 'options', 'sentiment', 'market'],
    featured: true,
    publishedAt: '2026-06-01'
  },
  {
    id: 'options-greeks',
    title: 'Understanding Options Greeks',
    description: 'Deep dive into Delta, Gamma, Theta, and Vega - essential concepts for options trading.',
    category: 'options',
    difficulty: 'advanced',
    duration: '15:30',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['greeks', 'delta', 'gamma', 'theta', 'vega'],
    featured: true,
    publishedAt: '2026-06-02'
  },
  {
    id: 'multi-leg-strategies',
    title: 'Multi-Leg Options Strategies',
    description: 'Build Bull Call Spreads, Iron Condors, Straddles, and more multi-leg options strategies.',
    category: 'options',
    difficulty: 'advanced',
    duration: '18:00',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['spreads', 'iron-condor', 'straddle', 'strangle'],
    featured: true,
    publishedAt: '2026-06-03'
  },
  {
    id: 'ai-signal-generator',
    title: 'AI Signal Generator Tutorial',
    description: 'How to use AI to detect 60+ candlestick patterns and generate trading signals with confidence scores.',
    category: 'ai',
    difficulty: 'beginner',
    duration: '11:00',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['ai', 'signals', 'patterns', 'machine-learning'],
    featured: true,
    publishedAt: '2026-06-04'
  },
  {
    id: 'ai-copilot',
    title: 'AI Copilot Walkthrough',
    description: 'Your personal trading assistant. Learn to use natural language to build strategies.',
    category: 'ai',
    difficulty: 'beginner',
    duration: '9:30',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['ai', 'copilot', 'nlp', 'chatbot'],
    featured: false,
    publishedAt: '2026-06-05'
  },
  {
    id: 'tradetron-setup',
    title: 'Setting Up TradetrON Integration',
    description: 'Step-by-step guide to connect ZO Bull with TradetrON for bi-directional trading.',
    category: 'tradetron',
    difficulty: 'intermediate',
    duration: '7:45',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['tradetron', 'integration', 'api', 'signals'],
    featured: true,
    publishedAt: '2026-06-06'
  },
  {
    id: 'pine-script-import',
    title: 'Importing Pine Script Strategies',
    description: 'Import your TradingView strategies into ZO Bull with full 110+ indicator support.',
    category: 'pine-script',
    difficulty: 'advanced',
    duration: '13:00',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['pine-script', 'tradingview', 'import', 'indicators'],
    featured: true,
    publishedAt: '2026-06-07'
  },
  {
    id: 'pine-script-basics',
    title: 'Pine Script Basics for Beginners',
    description: 'Learn Pine Script fundamentals - variables, functions, and plotting. A step-by-step guide to writing your first indicator.',
    category: 'pine-script',
    difficulty: 'beginner',
    duration: '16:30',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['pine-script', 'beginner', 'variables'],
    featured: false,
    publishedAt: '2026-06-10'
  },
  {
    id: 'pine-script-strategies',
    title: 'Building Strategies in Pine Script',
    description: 'Convert indicators to complete trading strategies with entry/exit conditions, stops, and take profits.',
    category: 'pine-script',
    difficulty: 'intermediate',
    duration: '20:00',
    thumbnail: PLACEHOLDER_THUMBNAIL,
    video: {
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      thumbnail: PLACEHOLDER_THUMBNAIL
    },
    tags: ['pine-script', 'strategy', 'entries'],
    featured: false,
    publishedAt: '2026-06-11'
  }
]

// Category Info
export const CATEGORIES: { id: TutorialCategory; name: string; icon: string; description: string }[] = [
  {
    id: 'getting-started',
    name: 'Getting Started',
    icon: '🚀',
    description: 'New to ZO Bull? Start here!'
  },
  {
    id: 'strategies',
    name: 'Strategies',
    icon: '📊',
    description: 'Learn to build trading strategies'
  },
  {
    id: 'brokers',
    name: 'Brokers',
    icon: '🔗',
    description: 'Connect your broker accounts'
  },
  {
    id: 'analytics',
    name: 'Analytics',
    icon: '📈',
    description: 'Understand your performance'
  },
  {
    id: 'advanced',
    name: 'Advanced',
    icon: '🎯',
    description: 'Advanced trading techniques'
  },
  {
    id: 'options',
    name: 'Options Trading',
    icon: '📊',
    description: 'Learn options trading - PCR, Greeks, multi-leg strategies'
  },
  {
    id: 'ai',
    name: 'AI Features',
    icon: '🤖',
    description: 'AI-powered trading tools and signals'
  },
  {
    id: 'tradetron',
    name: 'TradetrON',
    icon: '🔗',
    description: 'Connect with TradetrON for bi-directional trading'
  },
  {
    id: 'pine-script',
    name: 'Pine Script',
    icon: '📝',
    description: 'Import and use TradingView Pine Script strategies'
  },
  {
    name: 'Tips & Tricks',
    icon: '💡',
    description: 'Pro tips for power users'
  }
]

// Difficulty Colors
export const DIFFICULTY_COLORS: Record<TutorialDifficulty, string> = {
  beginner: '#10b981',   // Green
  intermediate: '#f59e0b', // Orange
  advanced: '#ef4444'     // Red
}

// Category Colors
export const CATEGORY_COLORS: Record<TutorialCategory, string> = {
  'getting-started': '#6366f1',
  'strategies': '#8b5cf6',
  'brokers': '#06b6d4',
  'analytics': '#10b981',
  'advanced': '#f59e0b',
  'tips': '#ec4899',
  'options': '#3b82f6',
  'ai': '#8b5cf6',
  'tradetron': '#f97316',
  'pine-script': '#22c55e'
}